import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { useGetOrders, useMatchOrder, useUnmatchOrder } from '@vindr/services/actions/order.action';
import { useGetDetailStudy } from '@vindr/services/actions/study.action';
import { GENDER } from '@vindr/services/constants/common.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { getDateFormatted } from '@vindr/utils/helpers/common.helper';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Select } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extendDateOptions } from '../../ui/DateRangePicker';
import Table from '../../ui/Table';
import OrderSearch from './OrderSearch';
import useOrderColumns from './useOrderColumns';

export interface MatchOrderFormProps {
  pendingOrderIds: string[];
  study: ReturnType<typeof useGetDetailStudy>['data']['data'][number];
  onFinish: () => void;
  onCancel: () => void;
  message: MessageInstance;
}

const MatchOrderForm = ({
  pendingOrderIds,
  study,
  onFinish,
  onCancel,
  message,
}: MatchOrderFormProps) => {
  const { t } = useTranslation('Vindoc');
  const [popupOrderSearch, setPopupOrderSearch] = useState(false);

  const [patientForm] = Form.useForm();
  const institution_id = Form.useWatch('institution_id', patientForm);
  const visit_id = Form.useWatch('visit_id', patientForm);

  const institutions = useGetInstitutions({ offset: 0, limit: 100 })?.data?.data || [];

  const { data: getMatchedOrders } = useGetOrders({
    offset: 0,
    limit: 100,
    study_id: study.study_id,
  });

  const matchedOrders = useMemo(() => getMatchedOrders?.data || [], [getMatchedOrders]);

  const [orderList, setOrderList] = useState(matchedOrders || []);
  const orderColumns = useOrderColumns({
    onRemove: (record) => {
      const newOrders = [...orderList];

      setOrderList(newOrders.filter((it) => it?.id != record?.id));
    },
  });

  const { data: getFindOrders } = useGetOrders({
    limit: 100,
    offset: 0,
    institution_ids: [study.institution_id],
    status: [ORDER_STATUS.ARRIVED, ORDER_STATUS.NEW],
  });

  const findOrders = useMemo(() => getFindOrders?.data, [getFindOrders]);

  const { mutateAsync: matchOrder } = useMatchOrder();
  const { mutateAsync: unmatchOrder } = useUnmatchOrder();

  useEffect(() => {
    if (orderList?.length) {
      const sex = orderList?.[0]?.visited_patient?.sex;
      const birth_date = orderList?.[0]?.visited_patient?.birth_date
        ? dayjs(orderList?.[0]?.visited_patient?.birth_date)
        : null;

      patientForm.setFieldsValue({
        ...orderList?.[0]?.visited_patient,
        sex: t(GENDER[sex]),
        birth_date,
        institution_id: orderList?.[0]?.institution_id,
      });
    } else {
      patientForm.resetFields();
      if (study) {
        patientForm.setFieldValue('institution_id', study?.institution_id);
      }
    }
  }, [orderList, patientForm, study, t]);

  useEffect(() => {
    let pendingOrders = [];

    if (pendingOrderIds?.length && findOrders) {
      pendingOrders = findOrders.filter((it) => pendingOrderIds.includes(it?.id));
    }
    setOrderList([...matchedOrders, ...pendingOrders]);
  }, [matchedOrders, pendingOrderIds, findOrders]);

  const handleSave = () => {
    patientForm
      .validateFields()
      .then(() => {
        const needMatchOrders = orderList.filter(
          (it) => !matchedOrders.find((od) => od?.id == it?.id)
        );
        const needUnmatchOrders = matchedOrders.filter(
          (it) => !orderList.find((od) => od?.id == it?.id)
        );
        const promisesMatch = needMatchOrders.map(async (order) =>
          matchOrder({ order_id: order.id, study_id: study.study_id })
        );
        const promisesUnmatch = needUnmatchOrders.map(async (order) =>
          unmatchOrder({ order_id: order.id, study_id: study.study_id })
        );
        const promises = [...promisesMatch, ...promisesUnmatch];

        Promise.all(promises)
          .then((res) => {
            if (needMatchOrders.length) {
              message.success(
                t(`{{ order_length }} order${needMatchOrders.length > 1 ? 's' : ''} matched`, {
                  'order_length': needMatchOrders.length,
                })
              );
            }
            if (needUnmatchOrders.length) {
              message.success(
                t(`{{ order_length }} order${needUnmatchOrders.length > 1 ? 's' : ''} unmatched`, {
                  'order_length': needUnmatchOrders.length,
                })
              );
            }
            onFinish();
          })
          .catch((error) => {
            const error_message =
              (error?.response?.data?.error_code &&
                MESSAGE.ERROR[error?.response?.data?.error_code]) ||
              MESSAGE.ERROR.EMSG_8005;

            message.error(t(error_message));
          });
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-col">
        <div className="border-t border-gray-600 pt-2 text-xl">{t('Patient Information')}</div>
        <div className="flex flex-col">
          <Form
            requiredMark
            form={patientForm}
            name="form"
            onFinish={handleSave}
            className="form-filter-condition"
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            labelAlign="left"
          >
            <Row gutter={[8, 8]}>
              <Col className="gutter-row" span={24}>
                <Form.Item
                  name="institution_id"
                  label={t('Institution')}
                  className="mb-2"
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 21 }}
                >
                  <Select placeholder={t('Select Institution')} size="small" disabled>
                    {institutions
                      ? institutions.map((it, idx) => (
                          <Select.Option key={idx} value={it?.id} title={it?.name}>
                            {it?.name}
                          </Select.Option>
                        ))
                      : null}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[8, 8]}>
              <Col className="gutter-row" span={8}>
                <Form.Item name="name" label={t('Patient Name')} className="mb-2">
                  <Input size="small" placeholder={t('Enter Patient Name')} disabled />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={8}>
                <Form.Item label={t('Gender')} className="mb-2" name="sex">
                  <Input size="small" placeholder={t('Enter Gender')} disabled />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={8}>
                <Form.Item name="birth_date" label={t('Birthday')} className="mb-2">
                  <DatePicker
                    size="small"
                    className="w-full"
                    format={getDateFormatted()}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[8, 8]}>
              <Col className="gutter-row" span={8}>
                <Form.Item name="patient_id" label={t('Patient ID')} className="mb-2">
                  <Input size="small" placeholder={t('Enter Patient ID')} disabled />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={8}>
                <Form.Item name="visit_id" label={t('Visit ID')} className="mb-2">
                  <Input size="small" placeholder={t('Enter Visit ID')} disabled />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={8}>
                {/* <Form.Item name="age" label={t('Age')} className="mb-2">
                  <InputNumber
                    size="small"
                    className="w-full"
                    placeholder={t('Enter Age')}
                    disabled
                  />
                </Form.Item> */}
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="border-t border-gray-600 py-2 text-xl">{t('Orders')}</div>
      <Table
        columns={orderColumns}
        dataSource={orderList}
        scroll={{ y: 500 }}
        pagination={null}
        wrapperClassName="h-auto"
      />
      <Button onClick={() => setPopupOrderSearch(true)}>{t('Add Order')}</Button>

      <Modal
        className="vindr-modal procedure-search max-w-screen w-[1200px]"
        title={t('Order Search')}
        open={popupOrderSearch}
        maskClosable={false}
        destroyOnClose
        zIndex={1001}
        onCancel={() => setPopupOrderSearch(false)}
        footer={null}
      >
        <OrderSearch
          initFilters={{
            institution_ids: institution_id ? [institution_id] : [],
            visit_id,
            scheduled_date_from: extendDateOptions['Last 7 days'][0].toISOString(),
            scheduled_date_to: extendDateOptions['Last 7 days'][1].toISOString(),
          }}
          message={message}
          selectedOrders={orderList}
          // matchedOrders={matchedOrders}
          onCancel={() => setPopupOrderSearch(false)}
          onSave={(orders) => {
            const newOrders = [];

            // get remain matchedOrder in orderList
            for (const od of matchedOrders) {
              if (orderList.find((it) => it?.id == od?.id)) {
                newOrders.push(od);
              }
            }
            // get new order selected
            for (const od of orders) {
              if (!newOrders.find((it) => it?.id == od?.id)) {
                newOrders.push(od);
              }
            }
            setOrderList(newOrders);
            setPopupOrderSearch(false);
          }}
        />
      </Modal>
      <div className="footer mt-3 flex justify-end">
        <Button onClick={onCancel}>{t('Cancel')}</Button>
        <Button type="primary" onClick={handleSave} className="ml-2">
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default MatchOrderForm;
